import React, {
  createRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { createRoot, Root } from 'react-dom/client'
import { BaseNotifier } from './baseNotifier'
import Snackbar from '../elementals/snackbar'

const DEFAULT_SNACKBAR_DURATION_MS = 5000

export interface SnackbarOptions {
  /** The message content of the snackbar */
  message: React.ReactNode
  /** Optional icon shown to the left of the message (e.g. 'eye-slash') */
  iconName?: string
  /** Optional action label (e.g. 'Undo'). Rendered as a button when provided. */
  actionLabel?: string
  /** Called when the action button is clicked (the snackbar closes first) */
  onAction?: () => void
  /** Whether the snackbar auto-dismisses after `duration` (default: true) */
  autoDismiss?: boolean
  /** Auto-dismiss duration in milliseconds (default: 5000) */
  duration?: number
}

interface ActiveSnackbar {
  options: SnackbarOptions
  /** Monotonic key so replacing the active snackbar restarts the timer */
  key: number
}

/**
 * Snackbars follow the "single instance" convention: opening a new one
 * replaces the active one and restarts the auto-dismiss timer. Flows that
 * need stacking should use the toast notifier instead.
 */
const SnackbarHost = forwardRef((_props, ref) => {
  const [active, setActive] = useState<ActiveSnackbar | null>(null)
  const keyRef = useRef(0)

  useEffect(() => {
    if (!active || active.options.autoDismiss === false) {
      return undefined
    }

    const timeoutId = setTimeout(() => {
      setActive(null)
    }, active.options.duration ?? DEFAULT_SNACKBAR_DURATION_MS)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [active])

  useImperativeHandle(ref, () => ({
    open: (options: SnackbarOptions) => {
      keyRef.current += 1
      setActive({ options, key: keyRef.current })
    },
    close: () => {
      setActive(null)
    },
  }))

  if (!active) {
    return null
  }

  const { message, iconName, actionLabel, onAction } = active.options

  return (
    <Snackbar
      fixed
      message={message}
      iconName={iconName}
      actionLabel={actionLabel}
      onAction={
        actionLabel
          ? () => {
              setActive(null)
              onAction?.()
            }
          : undefined
      }
    />
  )
})

SnackbarHost.displayName = 'SnackbarHost'

export class SnackbarNotifier extends BaseNotifier {
  private _containerDomNode: HTMLDivElement | null = null
  private _root: Root | null = null

  initialize(): void {
    const notificationSystem = createRef()

    // attach the host to the body so no ancestor transform breaks `fixed`
    this._containerDomNode = document.createElement('div')
    document.body.appendChild(this._containerDomNode)
    this._root = createRoot(this._containerDomNode)
    this._root.render(<SnackbarHost ref={notificationSystem} />)
    super.initialize(notificationSystem)
  }

  open(options: SnackbarOptions): void {
    super.open(options)
  }

  destroy(): void {
    // unmount and remove the container from the body
    if (this._containerDomNode) {
      this._root?.unmount()
      this._root = null
      document.body.removeChild(this._containerDomNode)
      this._containerDomNode = null
    }
    super.destroy()
  }
}
